import React, { useState, useCallback } from 'react';
import { Phone } from 'lucide-react';
import { formatPhoneInput, stripPhoneFormatting, normalizePhone, isValidPhone } from '../../utils/phoneUtils';

interface PhoneInputProps {
  /** Current value (can be E.164 format or formatted) */
  value: string;
  /** Called with the E.164 formatted value when changed */
  onChange: (value: string) => void;
  /** Called with the E.164 formatted value when blurred */
  onBlur?: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Additional CSS classes for the input */
  className?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether to show validation error styling */
  error?: boolean;
  /** Error message to display */
  errorMessage?: string;
  /** Input name */
  name?: string;
  /** Input id */
  id?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether to show the phone icon */
  showIcon?: boolean;
}

/**
 * A phone number input component that:
 * - Formats as the user types (e.g., (555) 123-4567)
 * - Validates phone numbers
 * - Returns normalized E.164 format to the parent
 */
const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = '(555) 123-4567',
  className = '',
  disabled = false,
  error = false,
  errorMessage,
  name,
  id,
  required = false,
  showIcon = true,
}) => {
  // Internal display value (formatted)
  const [displayValue, setDisplayValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Sync display value when controlled value prop changes
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    if (value) {
      if (value.startsWith('+')) {
        const digits = stripPhoneFormatting(value);
        const displayDigits = digits.length === 11 && digits.startsWith('1')
          ? digits.substring(1)
          : digits;
        setDisplayValue(formatPhoneInput(displayDigits));
      } else {
        setDisplayValue(formatPhoneInput(value));
      }
    } else {
      setDisplayValue('');
    }
  }

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Format the input
    const formatted = formatPhoneInput(inputValue);
    setDisplayValue(formatted);

    // Get raw digits
    const digits = stripPhoneFormatting(inputValue);

    // Normalize to E.164 if we have enough digits
    if (digits.length === 10) {
      const normalized = normalizePhone(digits);
      if (normalized) {
        onChange(normalized);
        setValidationError(null);
      } else {
        onChange(digits);
      }
    } else {
      // Pass the raw digits while typing
      onChange(digits);
    }
  }, [onChange]);

  const handleBlur = useCallback(() => {
    setTouched(true);

    // Get raw digits
    const digits = stripPhoneFormatting(displayValue);

    // Validate on blur
    if (digits.length > 0 && digits.length !== 10) {
      setValidationError('Phone number must be 10 digits');
    } else if (digits.length === 10 && !isValidPhone(digits)) {
      setValidationError('Invalid phone number');
    } else {
      setValidationError(null);
    }

    onBlur?.();
  }, [displayValue, onBlur]);

  const showError = error || (touched && validationError);
  const errorText = errorMessage || validationError;

  const inputClasses = `
    block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset
    focus:ring-2 focus:ring-inset
    ${showIcon ? 'pl-10 pr-3' : 'px-3'}
    ${showError
      ? 'ring-red-300 text-red-900 placeholder:text-red-300 focus:ring-red-500'
      : 'ring-gray-300 placeholder:text-gray-400 focus:ring-blue-600'
    }
    ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'text-gray-900'}
    sm:text-sm sm:leading-6
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className="relative">
      {showIcon && (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Phone
            className={`h-5 w-5 ${showError ? 'text-red-400' : 'text-gray-400'}`}
            aria-hidden="true"
          />
        </div>
      )}
      <input
        type="tel"
        id={id}
        name={name}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={inputClasses}
        {...(showError && { 'aria-invalid': 'true' })}
        aria-describedby={showError && errorText ? `${id || name}-error` : undefined}
      />
      {showError && errorText && (
        <p
          className="mt-1 text-sm text-red-600"
          id={`${id || name}-error`}
        >
          {errorText}
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
